import {
  Button,
  Flex,
  FlexItem,
  PageSection,
  PageSectionVariants,
  Tabs,
  Tab,
  TabTitleText,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { PatientCard } from '@app/components'
import { RequireFeatureFlag } from '@app/components/feature_flags/require-feature-flag'
import { AddContactCard } from '@app/components/patient/contact-card/add-contact-card'
import { EditableContactCard } from '@app/components/patient/contact-card/editable-contact-card'
import { ReadOnlyContactCard } from '@app/components/patient/contact-card/read-only-contact-card'
import { useFeatureFlags } from '@app/domain/feature-flags/feature-flags.hooks'
import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import {
  useAddPatientContactInformation,
  useGetPatient,
  useProgramEnrolments,
  useUpdatePatient,
  useUpdatePatientContactInformation,
  usePatientCommunicationConsent,
} from '@app/domain/patient/patient.hooks'
import type {
  IPatientEmailAddress,
  IPatientPhoneNumber,
  PhoneNumberTypeId,
} from '@app/domain/patient/patient.interface'
import { AppState } from '@app/state'
import * as Patients from '@app/state/patients'
import { PatientActionStatusId } from '@app/state/patients/state'
import { TabContent } from '@app/utils/styles/tab-content'

import { PatientMedicationHistoryTabContent } from './patient/patient-medication-history-tab-content'
import { ResponseHistoryTabContent } from './patient/response-history-tab-content'
import { VisitsAndProgramsTabContent } from './patient/visits-and-programs-tab-content'

interface RootParams {
  id: string
}

enum TabKeys {
  PatientMedicationHistory,
  ReponseHistory,
  VisitsAndPrograms,
}

export const PatientScreen = () => {
  const [activeTabKey, setActiveTabKey] = useState<number>(
    TabKeys.VisitsAndPrograms,
  )

  const setActiveTab = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab)
  }, [])

  const dispatch = useDispatch()
  const { updatePatient } = useUpdatePatient()
  const {
    updatePatientContactInformation,
  } = useUpdatePatientContactInformation()

  const featureFlags = useFeatureFlags()
  const isProgramConfigurationEnabled = featureFlags.isFeatureFlagEnabled(
    FeatureFlagId.ProgramConfiguration,
  )

  const { addContactInformationToPatient } = useAddPatientContactInformation()

  const { patient, getPatient, status: getPatientStatus } = useGetPatient()

  const { id } = useParams<RootParams>()
  const [contactIsEditable, setContactIsEditable] = useState(false)

  const [updatePatientStatus, setUpdatePatientStatus] = useState<
    PatientActionStatusId
  >()

  const [updateContactStatus, setUpdateContactStatus] = useState<
    PatientActionStatusId
  >()

  const [newContactFormIsVisible, setNewContactFormVisible] = useState(false)

  const { getPatientProgramEnrolments } = useProgramEnrolments()

  const {
    communicationConsent,
    getCommunicationConsent,
    updateEmailConsent,
    removeSmsConsent,
  } = usePatientCommunicationConsent()

  const incomingSmsNumber = useSelector(
    (state: AppState) => state.config.api?.intakeAnywhere?.incomingSmsNumber,
  )

  useEffect(() => {
    getPatient(id)
    getCommunicationConsent(id)
  }, [getPatient, getCommunicationConsent, id])

  const onRefresh = useCallback(() => {
    getPatientProgramEnrolments(id)
  }, [getPatientProgramEnrolments, id])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const handleUpdatePatient = useCallback(
    (id: string, patient: Patients.IApiUpdatePatient) => {
      updatePatient(id, patient)
        .then((response) => {
          getPatient(response.patient.id)
          setUpdatePatientStatus(PatientActionStatusId.Succeeded)
        })
        .catch(() => {
          setUpdatePatientStatus(PatientActionStatusId.Failed)
        })
    },
    [getPatient, updatePatient],
  )

  const handleUpdateContact = useCallback(
    (id: string, patient: Patients.IApiUpdateContactInformation) => {
      updatePatientContactInformation(id, patient)
        .then((response) => {
          setContactIsEditable(false)
          getPatient(response.patient.id)
          setUpdateContactStatus(PatientActionStatusId.Succeeded)
        })
        .catch(() => {
          setUpdateContactStatus(PatientActionStatusId.Failed)
        })
    },
    [getPatient, updatePatientContactInformation],
  )

  const handleAddContactInformation = useCallback(
    (
      email: IPatientEmailAddress,
      phone: IPatientPhoneNumber,
      phoneType: PhoneNumberTypeId,
    ) => {
      addContactInformationToPatient(id, email, phone, phoneType)
        .then(() => {
          getPatient(id)
          setUpdateContactStatus(PatientActionStatusId.Succeeded)
        })
        .catch(() => {
          setUpdateContactStatus(PatientActionStatusId.Failed)
        })
    },
    [id, getPatient, addContactInformationToPatient],
  )

  const handleSetContactIsEditable = useCallback(() => {
    setUpdateContactStatus(null)
    setContactIsEditable(true)
  }, [])

  const handleCancelEditableForm = useCallback(() => {
    setUpdateContactStatus(null)
    setContactIsEditable(false)
  }, [])

  const handleSetFormVisible = useCallback(() => {
    setNewContactFormVisible(true)
  }, [setNewContactFormVisible])

  const handleHideNewContactForm = useCallback(() => {
    setNewContactFormVisible(false)
  }, [setNewContactFormVisible])

  const handleClearActionStatus = useCallback(() => {
    dispatch(Patients.actions.clearActionStatus())
  }, [dispatch])

  if (getPatientStatus === 'success' && patient) {
    return (
      <>
        <PageSection>
          <Flex
            spaceItems={{ default: 'spaceItemsLg' }}
            direction={{ default: 'row' }}
          >
            <FlexItem>
              <PatientCard
                onUpdatePatient={handleUpdatePatient}
                patient={patient}
                updateStatus={updatePatientStatus}
                onClearStatus={handleClearActionStatus}
                isSelectPatientAllowed={false}
              />
            </FlexItem>
            <FlexItem>
              {patient.contactInformation ? (
                contactIsEditable ? (
                  <EditableContactCard
                    onCancel={handleCancelEditableForm}
                    onUpdate={handleUpdateContact}
                    patient={patient}
                    updateStatus={updateContactStatus}
                  />
                ) : (
                  <ReadOnlyContactCard
                    onStartEdit={handleSetContactIsEditable}
                    patient={patient}
                    updateStatus={updateContactStatus}
                    communicationConsent={communicationConsent}
                    onUpdateEmailConsent={updateEmailConsent}
                    onRemoveSmsConsent={removeSmsConsent}
                    incomingSmsNumber={incomingSmsNumber}
                  />
                )
              ) : newContactFormIsVisible ? (
                <AddContactCard
                  onAddContactInformation={handleAddContactInformation}
                  onCancel={handleHideNewContactForm}
                />
              ) : (
                <Button onClick={handleSetFormVisible} variant="secondary">
                  Add Contact Information
                </Button>
              )}
            </FlexItem>
          </Flex>
        </PageSection>
        <PageSection variant={PageSectionVariants.light}>
          <Tabs
            activeKey={activeTabKey}
            onSelect={setActiveTab}
            aria-label="Patient Screen Tabs"
            role="region"
          >
            {isProgramConfigurationEnabled ? (
              <Tab
                eventKey={TabKeys.VisitsAndPrograms}
                title={<TabTitleText>Visits & Programs</TabTitleText>}
                aria-label="Default content - Visits & Programs"
              />
            ) : (
              <Tab
                eventKey={TabKeys.VisitsAndPrograms}
                title={<TabTitleText>Visits</TabTitleText>}
                aria-label="Default content - Visits"
              />
            )}
            <Tab
              eventKey={TabKeys.ReponseHistory}
              title={<TabTitleText>Reponse History</TabTitleText>}
            />
            <RequireFeatureFlag
              key="medication_history"
              feature={FeatureFlagId.PatientMedicationHistory}
            >
              <Tab
                eventKey={TabKeys.PatientMedicationHistory}
                title={<TabTitleText>Medication History</TabTitleText>}
              />
            </RequireFeatureFlag>
          </Tabs>
          <TabContent>
            {activeTabKey === TabKeys.VisitsAndPrograms ? (
              <VisitsAndProgramsTabContent patientId={id} />
            ) : null}
            {activeTabKey === TabKeys.ReponseHistory ? (
              <ResponseHistoryTabContent patientId={id} />
            ) : null}
            {activeTabKey === TabKeys.PatientMedicationHistory ? (
              <PatientMedicationHistoryTabContent patientId={id} />
            ) : null}
          </TabContent>
        </PageSection>
      </>
    )
  } else if (getPatientStatus === 'loading') {
    return (
      <PageSection>
        <h1>Loading...</h1>
      </PageSection>
    )
  } else if (getPatientStatus === 'error') {
    return (
      <PageSection>
        <h1>No Patient Found</h1>
      </PageSection>
    )
  } else {
    return null
  }
}
